import { FormRestructureEvent } from "../core";
import { Logger } from "$lib/core";

const logger = new Logger("addTogether");

/**
 * @param {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement|null} element 
 * @returns {[(HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement)[], string[]]|null}
 */
function getTogetherFields(element) {
	if (!element) return null;
	const form = element.form;
	if (!form) return null;

	const togetherList = element.dataset.together;
	if (!togetherList) return null;

	const togetherNames = togetherList
		.split(",")
		.map(name => name.trim())
		.filter(Boolean);

	const togetherFields = togetherNames
		.map(name => {
			const control = /** @type {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement|null} */ (form.querySelector(`:scope [name="${name}"]`));
			if (control == null) {
				logger.error(`Could not find control matching data-together name '${name}' on control '${element.name}'`, { control: element, form });
			}
			return control;
		}).filter(c => c != null);
	
	return [togetherFields, togetherNames];
}

/**
 * 
 * @param {HTMLElement & { name: string }} control
 */
function generateDataTogetherAttribute(control) {
	if (control.dataset.together) return;

	const attrName = "data-val-remote-additionalfields";

	const attr = control.getAttribute(attrName) ?? "";
	if (!attr) return;

	const namePatterns = attr
		.split(",")
		.map(f => f.trim())
		.filter(Boolean);

	if (namePatterns.length === 0) return;

	const name = control.name;

	const lastDot = name.lastIndexOf(".");
	const modelBindingPrefix = lastDot !== -1 ? name.slice(0, lastDot) : "";
	const regex = /^\*\./;

	// Replace each "*.Field" with "prefix.Field" & filter out the current control's name if present.
	const together = namePatterns.map(pattern => {
		const rawName = pattern.replace(regex, "");
		if (rawName === name) return null;
		return modelBindingPrefix ? `${modelBindingPrefix}.${rawName}` : rawName;
	}).filter(s => s != null);

	const deduped = [...new Set(together)].join(",");
	control.setAttribute("data-together", deduped);
}

/**
 * Allows a field to specify a `data-together="Name1,Name2,etc"` attribute. So long as that attribute exists,
 * an invalid validation state for a field will automatically be deactivated (but not prevented) immediately after triggering
 * if the form controls referred to be name in the attribute value have not been filled in yet and have no value.
 * 
 * This extension was added to prevent aspnetcore errors from displaying in non-`bu-form` forms. Example:
 * FirstName and LastName. Can only submit if both are empty or both are filled. Use [Remote] validation to ensure this. Now it
 * marks FirstName as erroneous the moment you blur it by tabbing to the unfilled LastName. Needed to delay validation handling until
 * everything can be processed "together".
 * 
 * This is different from data-depends-on since the dependsOn extension will automatically hide/show or mark elements as readonly dynamically
 * in response to when a predicate is satisfied for a given field.
 * 
 * // TODO: Make this a more built-in feature of the validation service so that the validation state doesn't change in the first place.
 * // TODO: Make this more safe since it currently just blanket removes validation state, even if the validation error had nothing to do
 *          with the field's relationship to another field or being required (e.g. it could be a regex error instead or something).
 *          Actually, not sure if this TODO is necessary since it re-validates all "together" fields when any single one is changed/blurred.
 * 
 * @param {HTMLFormElement & { _togetherSet?: Set<string> }} form 
 */
export function addTogether(form) {
	if (!form._togetherSet) form._togetherSet = new Set();
	
	const togetherSet = form._togetherSet;
	const validationService = window.BU.forms.validationService;

	const observer = new MutationObserver(mutations => {
		for (const mutation of mutations) {
			if (mutation.type !== "attributes" || mutation.attributeName !== "class") continue;

			const span = /** @type {HTMLSpanElement} */ (mutation.target);

			if (!span.classList.contains("field-validation-error")) continue;

			const fieldName = span.getAttribute("data-valmsg-for");
			if (!fieldName) continue;

			const input = /** @type {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement|null} */ (form.querySelector(`:scope [name="${fieldName}"]`));
			if (!input) continue;

			const togetherData = getTogetherFields(input);
			if (togetherData == null) continue;
			const [togetherFields, togetherNames] = togetherData;

			const key = [input.name, ...togetherNames].join(",");

			const isNotEditedWithNoValue = (/** @type {typeof togetherFields[0]} */ other) => {
				return other &&
					other.dataset.edited !== "true" &&
					other.value.trim() === "";
			}
			const uneditedFieldsWithNoValue = togetherFields.map(isNotEditedWithNoValue);
			const shouldClearError = uneditedFieldsWithNoValue.some(x => x) && !uneditedFieldsWithNoValue.every(x => x);

			logger.debug("Evaluating input", input, shouldClearError);

			if (shouldClearError) {
				if (togetherSet.has(key)) continue;
				togetherSet.add(key);
				logger.debug("Clearing errors for input", input);
				validationService.removeError(input);

			}

			togetherSet.delete(key);
		}
	});

	form.addEventListener("restructure", event => {
		if (!(event instanceof FormRestructureEvent)) return;

		const controlSet = /** @type {WeakSet<HTMLElement>} */ (new WeakSet());

		// Refresh observation of all validation spans within the form.
		if (event.added.length > 0) {
			observer.disconnect();
			form.querySelectorAll(":scope span[data-valmsg-for]").forEach(span => {
				observer.observe(span, { attributes: true, attributeFilter: ["class"] });
			});

			const events = ["change", "blur"];
			for (const entry of event.added) {
				for (const element of entry.elements) {
					generateDataTogetherAttribute(element);
				}

				for (const element of entry.elements) {
					if (element.dataset.together) {
						for (const event of events) {
							element.addEventListener(event, ev => {
								const t = /** @type {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement|null} */ (ev.target);
								if (!t) return;
								if (t.dataset.edited !== "true") return;

								// Force re-evaluation of field validation even when the value is an empty string IF it has been edited.
								// Also, because these validation operations can stack up before being all processed at once, we throttle
								// them using `controlSet` so it only forcibly re-validates them once if necessary.
								const data = getTogetherFields(t);
								if (data == null) return;
								const [controls, _] = data;
								for (const c of controls) {
									if (c.dataset.edited !== "true") continue;
									if (controlSet.has(c)) continue;
									if (validationService?.logger) {
										validationService.logger.log(`Forcing validation of field '${c.name}' due to '${ev.type}' event on field '${t.name}'.`);
									}
									controlSet.add(c);
									validationService.validateField(c, (/** @type {boolean} */ success) => {
										if (validationService?.logger) {
											validationService.logger.log(`Forced validation for field ${c.name} result: ${success}`);
										}
										controlSet.delete(c);
									});
								}
							})
						}
					}
				}
			}
		}
	})
}
